import GradingState from '@/components/organisms/Grading/GradingState'
import { Button } from '@/components/atoms'
import { academyLearningScheduleApi } from 'services/api'
import { Dialog } from '@/components/organisms/Modal/Dialog'
import RecordingGradingContents from '@/components/Modal/RecordingGradingContents'
import { useState, useMemo } from 'react'
import ListenButton from '@/components/atoms/ListenButton/ListenButton'

interface GradingDictationProps {
  type?: 'scoreCard' | 'gradingCard'
  activity?: any
  responseJson?: string
  showTitle?: boolean
  recordingGradingResult?: 'correct' | 'incorrect'
  onRecordingGradingChange?: (status: 'correct' | 'incorrect' | undefined) => void
  canUseAi?: boolean
  recordingLocked?: boolean
}

const GradingDictation = ({ type = 'scoreCard', activity, responseJson, showTitle = true, recordingGradingResult, onRecordingGradingChange, canUseAi = true, recordingLocked = false }: GradingDictationProps ) => {

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentDialogStatus, setCurrentDialogStatus] = useState<'correct' | 'incorrect'>('correct');
  const [aiLoading, setAiLoading] = useState(false);

  // activity와 responseJson에서 실제 데이터 파싱
  const dictationData: {
    word: string;
    meaning: string;
    audioUrl: string | null;
    userInput: string;
    recordingUrl: string | null;
    correctAnswer: string;
    userAnswer: string;
    recordingStatus: 'correct' | 'word_incorrect' | 'recording_incorrect' | 'both_incorrect' | 'grading';
    writingStatus: 'correct' | 'word_incorrect' | 'recording_incorrect' | 'both_incorrect' | 'grading';
    charStatus: string[];
    itemKey: string;
  } = useMemo(() => {
    if (!activity) {
      // 더미 데이터
      return {
        word: "winter",
        meaning: "겨울",
        audioUrl: null,
        userInput: "wintr",
        recordingUrl: null,
        correctAnswer: "winter",
        userAnswer: "wintr",
        recordingStatus: 'grading' as 'correct' | 'word_incorrect' | 'recording_incorrect' | 'both_incorrect' | 'grading',
        writingStatus: 'word_incorrect' as 'correct' | 'word_incorrect' | 'recording_incorrect' | 'both_incorrect' | 'grading',
        charStatus: ['correct', 'correct', 'correct', 'correct', 'incorrect', 'missing_word'],
        itemKey: 'dictation-default'
      };
    }

    // activity.items[0].contentJson에서 플래시카드 데이터 가져오기
    const activityData = activity?.items?.[0] || {};
    let content: any = {};
    
    try {
      content = activityData.contentJson ? JSON.parse(activityData.contentJson) : {};
    } catch (e) {
      console.error('contentJson 파싱 에러:', e);
    }

    // 플래시카드 데이터 구조에 맞춰 파싱
    const word = content.word || content.sentence || content.text || 'winter';
    const meaning = content.translation || content.korean || content.meaning || '겨울';
    const audioUrl = content.audioUrl || content.audio || null;

    // responseJson 파싱
    let response: any = {};
    if (responseJson) {
      try {
        response = JSON.parse(responseJson);
      } catch (e) {
        console.error('responseJson 파싱 에러:', e);
      }
    }

    const userInput = response.userInput || '';
    const recordingUrl = response.recordingUrl || null;

    // 정답 체크
    const isTextCorrect = userInput.trim().toLowerCase() === word.trim().toLowerCase();

    // 녹음 상태 판단
    let recordingStatus: 'correct' | 'word_incorrect' | 'recording_incorrect' | 'both_incorrect' | 'grading' = 'grading';
    const itemKey = `dictation-${activity?.id || 'default'}`;
    
    if (recordingGradingResult !== undefined) {
      // 부모로부터 받은 채점 결과 사용
      recordingStatus = recordingGradingResult === 'correct' ? 'correct' : 'recording_incorrect';
    } else if (recordingUrl) {
      // 녹음이 있지만 채점하지 않았으면 "채점 필요"
      recordingStatus = 'grading';
    }

    // 글쓰기 상태 판단
    let writingStatus: 'correct' | 'word_incorrect' | 'recording_incorrect' | 'both_incorrect' | 'grading' = 'grading';
    if (userInput.trim().length > 0) {
      writingStatus = isTextCorrect ? 'correct' : 'word_incorrect';
    }

    // 각 글자별 상태 계산
    const charStatus: string[] = [];
    for (let i = 0; i < word.length; i++) {
      const correctChar = word[i];
      const userChar = userInput[i] || '';
      
      if (correctChar === ' ') {
        charStatus.push(userChar === ' ' ? 'space_correct' : 'space_incorrect');
      } else if (!userChar) {
        charStatus.push('missing_word');
      } else if (correctChar.toLowerCase() === userChar.toLowerCase()) {
        charStatus.push('correct');
      } else {
        charStatus.push('incorrect');
      }
    }

    return {
      word,
      meaning,
      audioUrl,
      userInput,
      recordingUrl,
      correctAnswer: word,
      userAnswer: userInput,
      recordingStatus,
      writingStatus,
      charStatus,
      itemKey
    };
  }, [activity, responseJson, recordingGradingResult])

  // 스펠링 검증 결과 렌더링 함수
  const renderDictationResult = () => {
    const result = [];
    
    // 정답 단어 길이만큼 반복
    for (let i = 0; i < dictationData.correctAnswer.length; i++) {
      const correctChar = dictationData.correctAnswer[i];
      const userChar = dictationData.userAnswer[i] || '';
      const status = dictationData.charStatus[i];
      
      if (status) {
        switch (status) {
          case 'correct':
            // 맞은 글자 - green-500 underline
            result.push(<span key={i} className='inline-block text-green-500 underline'>{userChar}</span>);
            break;
          case 'incorrect':
            // 틀린 글자 - red-500 underline
            result.push(<span key={i} className='text-red-500 underline'>{userChar}</span>);
            break;
          case 'missing_word':
            // 빠진 글자 - red-500 underline 공백
            result.push(<span key={i} className='text-transparent inline-block min-w-[6px] underline decoration-red-500'>{correctChar}</span>);
            break;
          case 'space_correct':
            // 맞은 띄어쓰기 - green-500
            result.push(<span key={i} className='text-transparent inline-block min-w-[6px] underline decoration-green-500'>{correctChar}</span>);
            break;
          case 'space_incorrect':
            // 틀린 띄어쓰기 - red-500
            result.push(<span key={i} className='text-transparent inline-block min-w-[6px] underline decoration-red-500'>{correctChar}</span>);
            break;
          default:
            result.push(<span key={i} className='text-gray-900'>{correctChar}</span>);
        }
      } else {
        // 상태가 없는 경우 기본 처리
        result.push(<span key={i} className='text-gray-900'>{correctChar}</span>);
      }
    }
    return result;
  };

  const handleOpenDialog = () => {
    // Dialog를 열 때 현재 채점 상태를 설정
    const currentStatus: 'correct' | 'incorrect' = recordingGradingResult || 'correct';
    setCurrentDialogStatus(currentStatus);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleConfirmGrading = () => {
    // 확인 버튼을 눌렀을 때 부모에게 알림
    console.log(`[GradingDictation] 녹음 채점 변경: activity.id=${activity?.id}, status=${currentDialogStatus}`);
    onRecordingGradingChange?.(currentDialogStatus);
    handleCloseDialog();
  };

  const handleGradingChange = (status: 'correct' | 'incorrect') => {
    console.log(`[GradingDictation] 라디오 버튼 변경: status=${status}`);
    setCurrentDialogStatus(status);
  };

  const recordingStatusForDisplay = aiLoading ? 'grading' : dictationData.recordingStatus;
  const canGradeRecording = type === 'gradingCard' && !recordingLocked;
  const showRecordingViewButton = type === 'scoreCard' || recordingLocked;
  const aiEnabled = canGradeRecording && canUseAi;
  const dialogReadOnly = type !== 'gradingCard' || recordingLocked;

  return (
    <div>
        <div className='flex flex-col gap-2'>
            {showTitle && (
              <p className='text-lg font-bold'>{activity?.title || '딕테이션'}</p>
            )}
            <div className='relative flex flex-col gap-2 justify-center items-center p-5 rounded-2xl border border-gray-200'>
              <p className='text-4xl font-bold'>{dictationData.correctAnswer}</p>
              <span className='text-sm text-gray-500'>{dictationData.meaning}</span>
              {dictationData.audioUrl && (
                <ListenButton audioSrc={dictationData.audioUrl} size={40} className='absolute top-2 right-2' />
              )}
            </div>
            <div className='flex justify-between items-center py-3 px-5 bg-gray-50 rounded-2xl border border-gray-200'>
                <p className='text-sm font-bold'>녹음한 답</p>
                <div className='flex gap-2'>
                    {dictationData.recordingUrl ? (
                      <>
                        {canGradeRecording && (
                          <div className='flex gap-2 items-center flex-wrap'>
                                {aiEnabled && (
                                  <Button size='small' onClick={async () => {
                                try {
                                  const scheduleId = (window as any).__CURRENT_SCHEDULE_ID__ || undefined;
                                  if (!scheduleId) {
                                    console.warn('scheduleId 미지정 - gradingDetail에서 내려 받은 값을 전역에 주입해주세요.');
                                  }
                                  setAiLoading(true);
                                  onRecordingGradingChange?.(undefined);
                                  const res = await academyLearningScheduleApi.aiGradeDictation(scheduleId, {
                                    recordingUrl: dictationData.recordingUrl || '',
                                    correctWord: dictationData.correctAnswer,
                                  });
                                  const ok = !!res?.data?.correct;
                                  setCurrentDialogStatus(ok ? 'correct' : 'incorrect');
                                  onRecordingGradingChange?.(ok ? 'correct' : 'incorrect');
                                } catch (e) {
                                  console.error('AI 채점 실패', e);
                                  alert('AI 채점에 실패했습니다.');
                                } finally {
                                  setAiLoading(false);
                                }
                              }} disabled={!!aiLoading}>{aiLoading ? '채점중...' : 'AI 채점 하기'}</Button>
                                )}
                              <Button size='small' intent='secondary' onClick={handleOpenDialog}>녹음 듣고 채점하기</Button>
                          </div>
                        )}
                        {showRecordingViewButton && (
                          <Button size='small' intent='secondary' onClick={handleOpenDialog}>녹음 듣기</Button>
                        )}
                      </>
                    ) : (
                      <span className='text-xs text-gray-500'>제출된 녹음이 없습니다.</span>
                    )}
                    <div className='w-[70px] flex-none flex items-center justify-center'>
                        <GradingState status={recordingStatusForDisplay} />
                    </div>  
                </div>
            </div>

                <div className={`overflow-hidden rounded-2xl border ${dictationData.writingStatus === 'correct' ? 'border-green-500' : dictationData.writingStatus === 'word_incorrect' || dictationData.writingStatus === 'recording_incorrect' || dictationData.writingStatus === 'both_incorrect' ? 'border-red-500' : 'border-gray-200'}`}>
                <div className='flex justify-between items-center px-4 py-2 w-full'>
                    <p className='text-sm font-bold text-gray-500'>입력한 답</p>
                    <GradingState status={dictationData.writingStatus} />
                </div>
            <div className='flex justify-center items-center py-3 bg-gray-50'>
                <p className='flex text-2xl font-bold'>
                    {renderDictationResult()}
                </p>
            </div>
            </div>
        </div>
        <Dialog
          open={isDialogOpen}
          onCancel={handleCloseDialog}
          onOk={dialogReadOnly ? handleCloseDialog : handleConfirmGrading}
          okText={dialogReadOnly ? '닫기' : '확인'}
          cancelText={dialogReadOnly ? undefined : '취소'}
        >
          <RecordingGradingContents 
            word={dictationData.correctAnswer}
            recordingUrl={dictationData.recordingUrl || undefined}
            initialGradingStatus={currentDialogStatus}
            onGradingChange={dialogReadOnly ? undefined : handleGradingChange}
            readOnly={dialogReadOnly}
          />
        </Dialog>
    </div>
  )
}

export default GradingDictation